import { fetchCouponGoods } from '@musinsa/apis-curator';
import {
  lazyPipe,
  validateServiceRequest,
  validateServiceResponse,
  withServiceErrorCapture,
} from '@musinsa/services-common-service-zod4';
import dayjs from 'dayjs';
import { getCouponExpirationText } from './coupon-expiration.util';
import {
  type GetCouponGoodsServiceRequest,
  GetCouponGoodsServiceRequestSchema,
  type GetCouponGoodsServiceResponse,
  GetCouponGoodsServiceResponseSchema,
} from './getCouponGoods.dto';

const formatDateRange = (start: string, end: string): string =>
  `${dayjs(start).format('YYYY/MM/DD')} ~ ${dayjs(end).format('YYYY/MM/DD')}`;

const isPendingByDate = (useStartDate: string): boolean =>
  dayjs(useStartDate).startOf('day').isAfter(dayjs().startOf('day'));

const isExpiredByDate = (useEndDate: string): boolean =>
  dayjs(useEndDate).startOf('day').isBefore(dayjs().startOf('day'));

const buildSubtitleText = (
  status: GetCouponGoodsServiceResponse['status'],
  isPending: boolean,
  useStartDate: string,
  useEndDate: string,
): string => {
  if (status === 'EXPIRED') return '쿠폰 사용기간 종료';
  if (isPending) return `진행 예정 | ${formatDateRange(useStartDate, useEndDate)}`;
  return getCouponExpirationText(useEndDate);
};

const transformApiResponseToServiceResponse = async (
  request: GetCouponGoodsServiceRequest,
): Promise<GetCouponGoodsServiceResponse> => {
  const response = await fetchCouponGoods(request);
  const { data } = response;
  const isPending = isPendingByDate(data.useStartDate);
  // NOTE: BE 가 내려준 EXPIRED/STOPPED 상태를 우선 적용하고, BE 가 EXPIRED 를 안 내려주는
  // 만료 캠페인만 useEndDate 로 보정한다 (사용중지·기간만료 모두 사용기간 종료 UI 로 통일).
  const status =
    data.status === 'EXPIRED' || data.status === 'STOPPED' || isExpiredByDate(data.useEndDate)
      ? ('EXPIRED' as const)
      : data.status;
  const hasNextPage = response.link?.next != null;
  const nextPage = hasNextPage ? (request.page ?? 1) + 1 : undefined;

  return {
    isOwner: data.isOwner,
    isPending,
    couponNo: data.couponNo,
    brandId: data.brandId,
    couponName: data.couponName,
    salesRate: data.salesRate,
    useStartDate: data.useStartDate,
    useEndDate: data.useEndDate,
    status,
    subtitleText: buildSubtitleText(status, isPending, data.useStartDate, data.useEndDate),
    list: data.list.map((item) => ({
      ...item,
      couponPrice: item.couponPrice ?? null,
      couponSalesRate: item.couponSalesRate ?? null,
      hasOptionPrice: item.hasOptionPrice ?? null,
      totalQuantity: item.totalQuantity ?? null,
      extraDiscountAmount: item.extraDiscountAmount ?? null,
      finalPrice: item.finalPrice ?? null,
      finalDiscount: item.finalDiscount ?? null,
    })),
    pagination: { hasNextPage, nextPage },
  };
};

export const getCouponGoodsService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(GetCouponGoodsServiceRequestSchema),
    transformApiResponseToServiceResponse,
    validateServiceResponse(GetCouponGoodsServiceResponseSchema),
  ),
);
